import React, { useEffect, useRef, useCallback, useState } from 'react';
import './PhoneMockup.css';

interface ChatStep {
  type: 'guest' | 'staff' | 'chalkboard' | 'optin';
  sender: string;
  content: string;
  color?: string;
  delay: number;
}

const GUEST_COLORS: Record<string, string> = {
  'accent-1': '#3b82f6',
  'accent-2': '#10b981',
  'accent-3': '#f97316',
  'accent-4': '#8b5cf6',
  'accent-5': '#14b8a6',
  'accent-6': '#ec4899',
};

const preambleScript: ChatStep[] = [
  { type: 'staff', sender: 'TC', content: "Welcome to Piedmont Brewhouse, we're so glad you're here! If there's anything you need during your visit, feel free to use this chat anytime.", delay: 1000 },
  { type: 'chalkboard', sender: '', content: '', delay: 2000 },
  { type: 'optin', sender: 'TC', content: 'Sign up now for our loyalty program and enjoy membership benefits and a free dessert tonight!', delay: 2000 },
];

const SESSION_COLORS = ['accent-1', 'accent-4', 'accent-2', 'accent-5'];

const conversations: ChatStep[][] = [
  // Conversation 1 — Simple service request + compliment with Google review link
  [
    { type: 'guest', sender: 'Guest', content: 'Hi, can we get some napkins please?', delay: 1000 },
    { type: 'guest', sender: 'Guest', content: 'And a refill too!', delay: 1000 },
    { type: 'staff', sender: 'TC', content: "Absolutely, I'll let the staff know that you need some extra napkins and a drink refill.", delay: 1800 },
    { type: 'guest', sender: 'Guest', content: 'Thank you!', delay: 1200 },
    { type: 'staff', sender: 'TC', content: "You're welcome! If you need anything else, just let me know.", delay: 1500 },
    { type: 'guest', sender: 'Guest', content: 'We love this place, the food is amazing!', delay: 2000 },
    { type: 'staff', sender: 'TC', content: "Thank you so much! We're thrilled you're enjoying your experience! We'd love for you to share your experience! You can leave us a review here:\nhttps://search.google.com/local/writereview?placeid=ChIJd4m0x7VYXogRqXdKFm04Q9k", delay: 2200 },
  ],
  // Conversation 2 — Guest A: Beers, ordering, sandwiches, chow chow, specials
  [
    { type: 'guest', sender: 'Guest', content: 'What beers do you have on tap?', delay: 1200 },
    { type: 'staff', sender: 'TC', content: "Here are our draft beer options:\n• Budweiser – $4.50\n• Bud Light – $4.50\n• Coors Light – $4.50\n• Blue Moon Belgian White – $6\n• SweetWater 420 Pale Ale – $6\n• Brooklyn Amber Lager – $6\n• Wiseacre Tiny Bomb Pilsner – $6\n• Hutton & Smith Igneous IPA – $6.50\n• Modelo Especial – $6\n• Corona Extra – $6\n• Guinness Draught – $6.75\n\nLet me know if you'd like to order one!", delay: 2500 },
    { type: 'guest', sender: 'Guest', content: 'Can we get 2 SweetWater?', delay: 1500 },
    { type: 'staff', sender: 'TC', content: "Great choice! I'll let the staff know you'd like to order two SweetWater 420s.", delay: 1800 },
    { type: 'guest', sender: 'Guest', content: 'Can you tell me about your sandwich options?', delay: 1500 },
    { type: 'staff', sender: 'TC', content: "Sure! Here are our sandwich options:\n\n• Brewhouse Signature Burger – $19\n  House pimento cheese, applewood bacon, fried onion straws, tangy chow-chow relish, and Creole mayo on a toasted brioche bun.\n\n• White Cheddar & Onion Burger – $18.50\n  Melted aged white cheddar, slow-cooked sweet onions, and roasted garlic aioli.\n\n• Piedmont Fried Chicken – $16.75\n  Crispy or grilled chicken breast with house hot sauce, seasoned pickles, and house mayo.\n\n• Firecracker Shrimp Tacos – $17\n  Three soft tortillas with fried shrimp tossed in spicy firecracker sauce and topped with crunchy slaw.\n\n• Southern Flounder Sandwich – $17\n  Cornmeal fried flounder, farmhouse slaw, and house tartar on a buttered bun.", delay: 2500 },
    { type: 'guest', sender: 'Guest', content: "Can the burger be ordered without chow chow? I don't know what that is.", delay: 1500 },
    { type: 'staff', sender: 'TC', content: "I'll have the staff check with the kitchen to see if the burger can be prepared without chow-chow for you.", delay: 1800 },
    { type: 'guest', sender: 'Guest', content: 'What is chow chow?', delay: 1200 },
    { type: 'staff', sender: 'TC', content: "Our house chow-chow is a Southern-style pickled relish made from chopped cabbage, peppers, and onions, brined for a tangy bite that cuts through rich meats and burgers.", delay: 2200 },
    { type: 'guest', sender: 'Guest', content: 'Oh ok. That actually sounds good!', delay: 1200 },
    { type: 'staff', sender: 'TC', content: "I'm glad to hear that! If you decide to try the burger or have any other questions, just let me know!", delay: 1800 },
    { type: 'guest', sender: 'Guest', content: 'Are there any specials offered for today other than the chalkboard?', delay: 1500 },
    { type: 'staff', sender: 'TC', content: "In addition to our chalkboard specials, we have our Weekly Specials:\n- Monday Burger & Brew Night – $14 for our Pied Burger with seasoned fries and a select draft pour or fountain drink.", delay: 2200 },
  ],
  // Conversation 3 — Simple service request
  [
    { type: 'guest', sender: 'Guest', content: 'Can we get more ketchup?', delay: 1000 },
    { type: 'staff', sender: 'TC', content: "Of course! I'll let the staff know to bring more ketchup to your table right away.", delay: 1800 },
    { type: 'guest', sender: 'Guest', content: 'Can you have the waitress bring over an extra set of silverware? We dropped a fork.', delay: 1500 },
    { type: 'staff', sender: 'TC', content: "Absolutely, I'll let the staff know to bring an extra set of silverware to your table right away.", delay: 1800 },
    { type: 'guest', sender: 'Guest', content: 'Perfect, thank you.', delay: 1200 },
    { type: 'staff', sender: 'TC', content: "You're very welcome! If you need anything else, just let me know.", delay: 1500 },
  ],
  // Conversation 4 — Guest B: Fish dishes, flounder allergen, wine pairing, wine list
  [
    { type: 'guest', sender: 'Guest', content: 'Can you tell me about your fish dishes?', delay: 1200 },
    { type: 'staff', sender: 'TC', content: "Certainly! Here are our fish dishes:\n\n• Blue Ridge Teriyaki Salmon Bowl – $25\n  Wood-grilled salmon glazed in sweet soy reduction over jasmine rice with carrots, cucumbers, edamame, and scallions.\n\n• Buttermilk Fried Carolina Flounder – $19\n  Cornmeal-crusted flounder fried golden, served with farmhouse slaw and hot hushpuppies.\n\n• Cajun Hearth-Grilled Salmon – $24\n  Flame-kissed salmon finished with whipped Creole herb butter and fresh lemon.\n\n• Coastal Shrimp & Cream Pasta – $26\n  Sautéed Gulf shrimp in parmesan cream sauce over linguine with blistered tomatoes and citrus butter.\n\n• Pan-Seared Snapper with Lemon & Capers – $34\n  Cast-iron seared snapper in a white wine, lemon, and caper pan sauce.", delay: 2500 },
    { type: 'guest', sender: 'Guest', content: 'The flounder sounds good, does it have any nuts on it?', delay: 1500 },
    { type: 'staff', sender: 'TC', content: "That's a great question! I'll have the staff check with the kitchen to confirm if the Buttermilk Fried Carolina Flounder contains any nuts or is prepared with any nut products.", delay: 2000 },
    { type: 'guest', sender: 'Guest', content: 'Thanks is there a wine that pairs well with the flounder?', delay: 1500 },
    { type: 'staff', sender: 'TC', content: "For a nice pairing with the Buttermilk Fried Flounder, I recommend our Kim Crawford Sauvignon Blanc — crisp and light, it complements the flavors beautifully. Our Kendall-Jackson Chardonnay would also work well.", delay: 2200 },
    { type: 'guest', sender: 'Guest', content: 'Hmm, ok maybe but can you show me what wines you have?', delay: 1500 },
    { type: 'staff', sender: 'TC', content: "Sure! Here are the wines we have available:\n\nSparkling\n• La Marca Prosecco – $12\n• Korbel Brut (House Bubbles) – $8\n\nWhite\n• Santa Margherita Pinot Grigio – $11\n• Chateau Ste. Michelle Riesling – $9\n• Kim Crawford Sauvignon Blanc – $12\n• Chardonnay, Kendall-Jackson – $13\n\nRed\n• Malbec, Trapiche Broquel – $11\n• Meiomi Pinot Noir – $13\n• Apothic Red Blend – $11\n• Josh Cellars Cabernet Sauvignon – $14", delay: 2500 },
    { type: 'guest', sender: 'Guest', content: 'Ok please let the staff know we are ready to order.', delay: 1500 },
    { type: 'staff', sender: 'TC', content: "Absolutely! I'll let the staff know that you're ready to place your order.", delay: 1800 },
  ],
];

const PhoneMockup: React.FC = () => {
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const lastTapRef = useRef(0);

  const handleDoubleTap = useCallback((e: React.TouchEvent) => {
    const now = Date.now();
    if (now - lastTapRef.current < 350) {
      e.preventDefault();
      setIsFullscreen(prev => !prev);
    }
    lastTapRef.current = now;
  }, []);

  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isFullscreen]);

  const delay = useCallback((ms: number) => {
    return new Promise<void>((resolve, reject) => {
      const id = setTimeout(() => {
        if (cancelRef.current) reject(new Error('cancelled'));
        else resolve();
      }, ms);
      if (cancelRef.current) {
        clearTimeout(id);
        reject(new Error('cancelled'));
      }
    });
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = chatBodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const appendHTML = useCallback((html: string) => {
    const el = chatBodyRef.current;
    if (!el) return;
    el.insertAdjacentHTML('beforeend', html);
    scrollToBottom();
  }, [scrollToBottom]);

  const addMessage = useCallback((sender: string, content: string, type: string, color?: string) => {
    const isGuest = type === 'guest';
    const bgHex = color ? GUEST_COLORS[color] || '' : '';
    const style = isGuest && bgHex ? ` style="background-color: ${bgHex};"` : '';
    const bubbleClass = isGuest ? 'phone-bubble guest-bubble' : 'phone-bubble staff-bubble';
    const rowClass = isGuest ? 'phone-msg-row guest' : 'phone-msg-row staff';
    const formatted = content.replace(/\n/g, '<br>');

    appendHTML(`
      <div class="${rowClass}">
        <div class="${bubbleClass}"${style}>
          <span class="bubble-sender">${sender}:</span> ${formatted}
        </div>
      </div>
    `);
  }, [appendHTML]);

  const addOptIn = useCallback((sender: string, content: string) => {
    appendHTML(`
      <div class="phone-msg-row staff">
        <div class="phone-bubble staff-bubble">
          <span class="bubble-sender">${sender}:</span> ${content}
          <button class="phone-join-btn">Join</button>
        </div>
      </div>
    `);
  }, [appendHTML]);

  const addStartedScreen = useCallback(() => {
    appendHTML(`
      <div class="phone-started-screen">
        <div class="phone-started-title">TableChat<sup>™</sup> Started</div>
        <div class="phone-started-subtitle">Send a message to your server below in your language of choice.</div>
      </div>
    `);
  }, [appendHTML]);

  const removeStartedScreen = useCallback(() => {
    const el = chatBodyRef.current;
    if (!el) return;
    const screen = el.querySelector('.phone-started-screen');
    if (screen) screen.remove();
  }, []);

  const addChalkboard = useCallback(() => {
    appendHTML(`
      <div class="phone-chalkboard">
        <div class="phone-chalkboard-title">Today's Specials</div>
        <div class="phone-chalkboard-item">Pied Burger & Fries $12</div>
        <div class="phone-chalkboard-item">$2 off pitchers of beer 4-6pm</div>
        <div class="phone-chalkboard-item">Half-price wings till 6pm</div>
        <div class="phone-chalkboard-item">Dessert + coffee combo $8 after 8pm</div>
        <div class="phone-chalkboard-footer">🎵 Live Music every Thursday @ 9pm</div>
        <div class="phone-chalkboard-dust"></div>
      </div>
    `);
  }, [appendHTML]);

  useEffect(() => {
    cancelRef.current = false;

    const run = async () => {
      let idx = 0;
      try {
        while (!cancelRef.current) {
          const body = chatBodyRef.current;
          if (!body) return;
          body.innerHTML = '';

          addStartedScreen();
          await delay(2500);
          removeStartedScreen();

          for (const step of preambleScript) {
            await delay(step.delay);
            if (step.type === 'chalkboard') addChalkboard();
            else if (step.type === 'optin') addOptIn(step.sender, step.content);
            else addMessage(step.sender, step.content, step.type, step.color);
          }

          const script = conversations[idx];
          const sessionColor = SESSION_COLORS[idx % SESSION_COLORS.length];
          for (const step of script) {
            await delay(step.delay);
            const color = step.type === 'guest' ? sessionColor : step.color;
            addMessage(step.sender, step.content, step.type, color);
          }

          await delay(1000);
          appendHTML(`<div class="phone-session-separator">— Session Closed &amp; Log Cleared for New Guests —</div>`);
          await delay(2000);

          idx = (idx + 1) % conversations.length;
          await delay(1000);
        }
      } catch {
        // cancelled on unmount
      }
    };

    run();

    return () => {
      cancelRef.current = true;
    };
  }, [delay, addMessage, addOptIn, addChalkboard, addStartedScreen, removeStartedScreen, appendHTML]);

  return (
    <div
      className={`phone-mockup${isFullscreen ? ' phone-fullscreen' : ''}`}
      onTouchEnd={handleDoubleTap}
    >
      <div className="phone-chat-container">
        {/* Top Bar */}
        <div className="phone-top-bar">
          <div className="phone-top-bar-left">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          </div>
          <div className="phone-top-bar-right">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span className="phone-notification-badge">2</span>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" /></svg>
          </div>
        </div>

        {/* App Bar */}
        <div className="phone-app-bar">
          <div className="phone-app-bar-left">
            <div className="phone-avatar">TC</div>
            <div>
              <div className="phone-app-bar-name">
                TC <span className="phone-online-dot">(Online)</span>
              </div>
              <div className="phone-table-label">Table 1</div>
            </div>
          </div>
          <div className="phone-app-bar-right">
            <div>
              <div className="phone-restaurant-name">Piedmont Brewhouse</div>
              <div className="phone-restaurant-location">Atlanta, Georgia</div>
            </div>
            <div className="phone-restaurant-icon">
              <img src="/piedmont-brewhouse-logo.png" alt="Piedmont Brewhouse" className="phone-restaurant-logo" />
            </div>
          </div>
        </div>

        {/* Chat Body */}
        <div className="phone-chat-body" ref={chatBodyRef} />

        {/* Input Bar */}
        <div className="phone-input-bar">
          <div className="phone-input-inner">
            <span className="phone-input-placeholder">Message TC...</span>
            <svg className="phone-send-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneMockup;
